<template>
  <div class="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
    <div class="w-full max-w-xl bg-gray-900 text-white rounded-lg shadow-2xl border border-gray-800 overflow-hidden">
      <!-- Header -->
      <header class="px-5 py-4 bg-indigo-900 border-b border-gray-800">
        <div class="flex items-center justify-between">
          <h2 class="text-lg font-bold text-indigo-300 flex items-center space-x-2">
            <i class="fas fa-robot"></i>
            <span>หุ่นยนต์สังหารที่กำลังทำงาน</span>
          </h2>

          <span class="inline-flex items-center space-x-2 text-xs px-2 py-1 rounded-md border border-indigo-500 bg-indigo-900 bg-opacity-30 text-indigo-200">
            <i class="fas fa-list-ol"></i>
            <span>{{ activeRobots?.length || 0 }} ตัว</span>
          </span>
        </div>
      </header>

      <!-- Body -->
      <section class="p-5">
        <div class="overflow-y-auto max-h-96 pr-1" id="style-1">
          <!-- Empty state -->
          <div
            v-if="!activeRobots || activeRobots.length === 0"
            class="text-center text-indigo-300 text-sm py-10 opacity-80"
          >
            <i class="fas fa-satellite-dish text-2xl mb-2"></i>
            <div>ไม่มีหุ่นยนต์สังหารที่กำลังทำงาน</div>
          </div>

          <!-- Robot cards -->
          <article
            v-for="robot in activeRobots"
            :key="robot.id"
            class="bg-gray-800 rounded-lg p-4 border border-gray-700 mb-4 hover:border-indigo-600 transition-colors duration-200"
          >
            <!-- Top row -->
            <div class="flex items-center space-x-3">
              <img
                :src="robot.img || fallbackRobot"
                alt="Robot"
                class="w-16 h-16 rounded-md object-cover border-2 border-purple-500"
                @error="onRobotImgError"
              >

              <div class="flex-1 min-w-0">
                <h3 class="text-base font-semibold text-purple-300 truncate">
                  {{ robot.name || 'หุ่นยนต์สังหาร' }}
                </h3>

                <div class="flex flex-wrap items-center space-x-2 mt-1">
                  <span class="text-xs px-2 py-0.5 rounded-md border border-gray-700 bg-gray-700 text-indigo-200">
                    <i class="fas fa-map-marker-alt mr-1"></i>{{ robot.map || 'ไม่ระบุ' }}
                  </span>

                  <span class="text-xs px-2 py-0.5 rounded-md border border-gray-700 bg-gray-700 text-indigo-200">
                    <i class="fas fa-hourglass-half mr-1"></i>{{ elapsedHours(robot) }} ชม.
                  </span>

                  <span
                    v-if="robot.searched"
                    class="text-xs px-2 py-0.5 rounded-md border border-yellow-600 bg-yellow-900 text-yellow-300"
                  >
                    <i class="fas fa-search mr-1"></i> ค้นหาแล้ว
                  </span>
                </div>
              </div>
            </div>

            <!-- HP Bar -->
            <div class="mt-3">
              <div class="relative h-5 rounded bg-gray-700 overflow-hidden border border-gray-600">
                <div
                  class="h-5 transition-all duration-200"
                  :class="hpBarClass(robot)"
                  :style="{ width: `${hpPct(robot)}%` }"
                ></div>

                <p class="absolute inset-0 flex items-center justify-center text-xxs tracking-wide">
                  HP: {{ currentHp(robot) }} / {{ calculatedStats(robot).maxHp }}
                </p>
              </div>
            </div>

            <!-- Stats -->
            <div class="grid grid-cols-2 gap-2 mt-3 text-sm">
              <div class="bg-gray-700 p-2 rounded-md">
                <i class="fas fa-fist-raised text-indigo-400 mr-1"></i>
                โจมตี:
                <b class="float-right">{{ calculatedStats(robot).atk }}</b>
              </div>

              <div class="bg-gray-700 p-2 rounded-md">
                <i class="fas fa-bolt text-indigo-400 mr-1"></i>
                คริติคอล:
                <b class="float-right">{{ calculatedStats(robot).critical }}%</b>
              </div>

              <div class="bg-gray-700 p-2 rounded-md">
                <i class="fas fa-wind text-indigo-400 mr-1"></i>
                หลบหลีก:
                <b class="float-right">{{ calculatedStats(robot).evasion }}%</b>
              </div>

              <div class="bg-gray-700 p-2 rounded-md">
                <i class="fas fa-shield-alt text-indigo-400 mr-1"></i>
                ป้องกัน:
                <b class="float-right">{{ calculatedStats(robot).defense }}</b>
              </div>
            </div>

            <!-- Farming / Search -->
            <div class="mt-3 space-y-2">
              <template v-if="!robot.searched">
                <div
                  v-if="elapsedHours(robot) >= 3"
                  class="text-xs text-indigo-300 text-center"
                >
                  สำรวจมาแล้ว {{ elapsedHours(robot) }} ชั่วโมง • โอกาสดรอปดีขึ้น
                  <b>{{ bonusPct(robot) }}%</b>
                </div>

                <div
                  v-else
                  class="text-xs text-yellow-300 text-center"
                >
                  เหลืออีก {{ remainingToSearch(robot) }} ถึงจะค้นหาได้
                </div>

                <div class="flex justify-center">
                  <button
                    @click="searchItem(robot.id)"
                    class="bg-blue-600 hover:bg-blue-700 text-white py-1 px-3 rounded text-sm transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                    :disabled="elapsedHours(robot) < 3 || !!isSearching[robot.id]"
                  >
                    <span v-if="!isSearching[robot.id]">
                      <i class="fas fa-search mr-1"></i>
                      ค้นหาไอเทม
                    </span>

                    <span
                      v-else
                      class="inline-block w-4 h-4 rounded-full border-2 border-white border-opacity-40 border-t-white animate-spin"
                    ></span>
                  </button>
                </div>
              </template>

              <template v-else>
                <div class="flex items-start justify-between space-x-3">
                  <div class="text-xs">
                    <div class="text-indigo-200 font-medium mb-1">
                      <i class="fas fa-gem mr-1"></i>
                      พบของรางวัลแล้ว
                    </div>

                    <div class="text-yellow-300">
                      กรุณาเรียกคืนภายใน
                      <b>{{ getRemainingDeadline(robot) }}</b>
                    </div>
                  </div>

                  <button
                    @click="openInventoryModal(robot.inventory, robot.diamondReward)"
                    class="bg-indigo-600 hover:bg-indigo-700 text-white py-1 px-3 rounded text-sm transition-colors duration-200"
                  >
                    ดูไอเทม
                  </button>
                </div>
              </template>
            </div>

            <!-- Legacy inventory warning -->
            <div
              v-if="hasLegacyInventory(robot)"
              class="mt-3 rounded-md border border-yellow-700 bg-yellow-900 bg-opacity-40 p-3 text-xs text-yellow-100 space-y-2"
            >
              <div>
                <i class="fas fa-exclamation-triangle mr-1"></i>
                พบไอเทมรางวัลรูปแบบเก่า {{ legacyInventoryCount(robot) }} ชิ้น ต้องแปลงเป็น v2 ก่อนเรียกคืน
              </div>

              <button
                @click="convertInventory(robot.id)"
                class="w-full bg-yellow-600 hover:bg-yellow-700 text-white py-1.5 px-3 rounded text-sm transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                :disabled="!!isConverting[robot.id]"
              >
                <span v-if="!isConverting[robot.id]">
                  <i class="fas fa-sync-alt mr-1"></i>
                  แปลงรางวัลเป็น v2
                </span>

                <span
                  v-else
                  class="inline-block w-4 h-4 rounded-full border-2 border-white border-opacity-40 border-t-white animate-spin"
                ></span>
              </button>
            </div>

            <!-- Recall -->
            <div class="mt-3 space-y-2">
              <div
                class="text-center text-xs"
                :class="hasLegacyInventory(robot) ? 'text-yellow-300' : isRecallable(robot) ? 'text-green-300' : 'text-gray-300'"
              >
                <i class="fas fa-clock mr-1"></i>{{ recallStatusText(robot) }}
              </div>

              <div class="flex space-x-2">
                <button
                  v-if="!confirmRecall[robot.id]"
                  @click="toggleConfirmRecall(robot.id)"
                  class="bg-red-600 hover:bg-red-700 text-white py-1 px-3 rounded text-sm flex-1 disabled:opacity-60 disabled:cursor-not-allowed"
                  :disabled="!isRecallable(robot) || hasLegacyInventory(robot) || !!isRecalling[robot.id]"
                >
                  เรียกคืน
                </button>

                <div
                  v-else
                  class="flex space-x-2 w-full"
                >
                  <button
                    @click="confirmRecallAction(robot.id)"
                    class="bg-red-700 hover:bg-red-800 text-white py-1 px-3 rounded text-sm flex-1 disabled:opacity-60 disabled:cursor-not-allowed"
                    :disabled="!isRecallable(robot) || hasLegacyInventory(robot) || !!isRecalling[robot.id]"
                  >
                    <span v-if="!isRecalling[robot.id]">
                      <i class="fas fa-check mr-1"></i>
                      ยืนยันเรียกคืน
                    </span>

                    <span
                      v-else
                      class="inline-block w-4 h-4 rounded-full border-2 border-white border-opacity-40 border-t-white animate-spin"
                    ></span>
                  </button>

                  <button
                    @click="toggleConfirmRecall(robot.id)"
                    class="bg-gray-700 hover:bg-gray-600 text-gray-200 py-1 px-3 rounded text-sm flex-1"
                  >
                    ยกเลิก
                  </button>
                </div>
              </div>
            </div>
          </article>
        </div>

        <!-- Error Message -->
        <div
          v-if="errorMessage"
          class="mt-4 rounded-md border border-red-700 bg-red-900 bg-opacity-40 text-red-200 text-sm p-3 text-center"
        >
          <i class="fas fa-exclamation-circle mr-1"></i>
          {{ errorMessage }}
        </div>

        <!-- Success Message -->
        <div
          v-if="successMessage"
          class="mt-4 rounded-md border border-green-700 bg-green-900 bg-opacity-40 text-green-200 text-sm p-3 text-center"
        >
          <i class="fas fa-check-circle mr-1"></i>
          {{ successMessage }}
        </div>

        <!-- Footer -->
        <div class="mt-4 flex items-center justify-center">
          <button
            @click="$emit('close')"
            class="w-full sm:w-1/2 py-2 text-sm sm:text-base rounded-lg font-semibold transition-all duration-200 bg-gray-700 hover:bg-gray-600"
          >
            <i class="fas fa-eye-slash text-xs mr-2"></i>
            ซ่อน
          </button>
        </div>
      </section>
    </div>
  </div>

  <!-- Inventory Modal -->
  <div
    v-if="showInventoryModal"
    class="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-sm flex items-center justify-center p-4 z-50"
  >
    <div class="w-full max-w-sm bg-gray-900 text-white rounded-lg p-6 border border-gray-800 shadow-xl">
      <h2 class="text-center text-lg font-bold text-indigo-300 mb-3">
        ไอเทมที่พบ
      </h2>

      <div class="overflow-y-auto max-h-80 pr-1" id="style-1">
        <div
          v-if="selectedDiamond > 0"
          class="flex items-center space-x-3 bg-gray-800 rounded-md p-3 mb-2 border border-indigo-700"
        >
          <img
            src="https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/item2%2F%E0%B8%97%E0%B8%B1%E0%B9%88%E0%B8%A7%E0%B9%84%E0%B8%9B%2F%E0%B9%80%E0%B8%9E%E0%B8%8A%E0%B8%A3.png?alt=media&token=d0f95851-b518-46cd-aa78-4729cc6d9f41"
            alt="Diamond"
            class="w-12 h-12 rounded-md object-cover border-2 border-indigo-500"
          >

          <div class="flex-1">
            <h3 class="text-sm font-semibold text-indigo-200">
              เพชร x {{ selectedDiamond }}
            </h3>
            <p class="text-xs text-gray-400">
              ประเภท: สกุลเงิน
            </p>
          </div>
        </div>

        <div
          v-for="(item, idx) in selectedInventory"
          :key="idx"
          class="flex items-center space-x-3 bg-gray-800 rounded-md p-3 mb-2 border border-gray-700"
        >
          <img
            :src="item.img || fallbackItem"
            alt="Item"
            class="w-12 h-12 rounded-md object-cover border-2 border-indigo-500"
            @error="onItemImgError"
          >

          <div class="flex-1 min-w-0">
            <h3 class="text-sm font-semibold text-indigo-200 truncate">
              {{ item.name }}
            </h3>
            <p class="text-xs text-gray-400">
              ประเภท: {{ item.type || 'ไม่ระบุ' }}
            </p>
          </div>
        </div>

        <p
          v-if="selectedInventory.length === 0 && selectedDiamond === 0"
          class="text-center text-gray-400 text-sm py-6"
        >
          ไม่พบไอเทม
        </p>
      </div>

      <button
        @click="showInventoryModal = false"
        class="w-full mt-3 bg-indigo-600 hover:bg-indigo-700 text-white py-2 rounded-md transition-colors duration-200"
      >
        ปิด
      </button>
    </div>
  </div>
</template>

<script>
import { projectFunctions } from '@/firebase/config'
import { ref, onUnmounted } from 'vue'
import gameItems from '@/data/gameItems.json'
import defaultImg from '@/assets/default-item.png'

export default {
  name: 'ActiveRobotsModal',

  emits: ['close'],

  props: {
    activeRobots: {
      type: Array,
      required: true
    }
  },

  setup() {
    const now = ref(Date.now())
    const tick = setInterval(() => {
      now.value = Date.now()
    }, 1000)

    const isRecalling = ref({})
    const isSearching = ref({})
    const isConverting = ref({})
    const confirmRecall = ref({})

    const showInventoryModal = ref(false)
    const selectedInventory = ref([])
    const selectedDiamond = ref(0)

    const errorMessage = ref('')
    const successMessage = ref('')

    const gameActions = projectFunctions.httpsCallable('gameActions')

    const fallbackRobot = defaultImg
    const fallbackItem = defaultImg

    const clearMessages = () => {
      errorMessage.value = ''
      successMessage.value = ''
    }

    const setErrorFromCallable = (error, fallback = 'เกิดข้อผิดพลาด') => {
      console.error(error)
      successMessage.value = ''
      errorMessage.value =
        error?.message ||
        error?.details?.message ||
        error?.details ||
        fallback
    }

    const setSuccess = (message) => {
      errorMessage.value = ''
      successMessage.value = message || 'สำเร็จ'

      setTimeout(() => {
        if (successMessage.value === message) {
          successMessage.value = ''
        }
      }, 2500)
    }

    const onRobotImgError = (e) => {
      e.target.src = fallbackRobot
    }

    const onItemImgError = (e) => {
      e.target.src = fallbackItem
    }

    onUnmounted(() => {
      clearInterval(tick)
    })

    const elapsedHours = (r) => {
      return Math.max(
        0,
        Math.floor((now.value - (r.deployedAt?.toMillis?.() || 0)) / 3600000)
      )
    }

    const bonusPct = (r) => {
      return Math.min(100, Math.max(0, (elapsedHours(r) - 3) * 10))
    }

    const calculatedStats = (r) => {
      const base = r.stats || {}
      const item = r.itemUpgradeStats || {}
      const silver = r.silverUpgradeStats || {}

      const st = (base.stamina || 0) + (item.stamina || 0) + (silver.stamina || 0)
      const str = (base.str || 0) + (item.str || 0) + (silver.str || 0)
      const int = (base.int || 0) + (item.int || 0) + (silver.int || 0)
      const agi = (base.agi || 0) + (item.agi || 0) + (silver.agi || 0)
      const df = (base.defense || 0) + (item.defense || 0) + (silver.defense || 0)

      return {
        maxHp: st * 20 + (base.maxHp || 0),
        atk: str * 2 + (base.atk || 0),
        critical: Math.floor(int / 2) + (base.critical || 0),
        evasion: Math.floor(agi / 2) + (base.evasion || 0),
        defense: df
      }
    }

    const currentHp = (r) => {
      return Math.max(0, (calculatedStats(r).maxHp || 0) - (r.damageTaken || 0))
    }

    const hpPct = (r) => {
      const max = calculatedStats(r).maxHp || 0
      return max > 0
        ? Math.max(0, Math.min(100, (currentHp(r) / max) * 100))
        : 0
    }

    const hpBarClass = (r) => {
      const p = hpPct(r)

      if (p > 50) return 'bg-indigo-500'
      if (p > 20) return 'bg-yellow-500'
      return 'bg-red-600'
    }

    const isInventoryItemV2 = (item) => {
      return !!item &&
        typeof item === 'object' &&
        !Array.isArray(item) &&
        typeof item.templateId === 'string' &&
        !!item.templateId &&
        !!item.states &&
        typeof item.states === 'object' &&
        !Array.isArray(item.states)
    }

    const legacyInventoryCount = (robot) => {
      const inventory = Array.isArray(robot?.inventory) ? robot.inventory : []
      return inventory.filter(item => !isInventoryItemV2(item)).length
    }

    const hasLegacyInventory = (robot) => {
      return legacyInventoryCount(robot) > 0
    }

    const isRecallable = (r) => {
      const availableAt = r.recallAvailableAt?.toMillis?.() || 0
      const available = now.value >= availableAt

      if (r.recallDeadline?.toMillis) {
        const deadline = r.recallDeadline.toMillis()
        return available && now.value <= deadline
      }

      return available
    }

    const getRemainingTime = (r) => {
      const availableAt = r.recallAvailableAt?.toMillis?.() || 0
      const remaining = availableAt - now.value

      if (remaining <= 0) return 'พร้อมเรียกคืน'

      const h = Math.floor(remaining / 3600000)
      const m = Math.floor((remaining % 3600000) / 60000)
      const s = Math.floor((remaining % 60000) / 1000)

      return `เหลือ ${h} ชม. ${m} น. ${s} ว.`
    }

    const recallStatusText = (r) => {
      if (hasLegacyInventory(r)) {
        return 'ต้องแปลงไอเทมรางวัลเป็น v2 ก่อนเรียกคืน'
      }

      if (!isRecallable(r)) {
        const txt = getRemainingTime(r)

        if (r.recallDeadline?.toMillis && now.value > r.recallDeadline.toMillis()) {
          return 'หมดเวลาเรียกคืน'
        }

        return txt
      }

      if (r.recallDeadline?.toMillis) {
        const remaining = r.recallDeadline.toMillis() - now.value

        if (remaining <= 0) return 'หมดเวลาเรียกคืน'

        const h = Math.floor(remaining / 3600000)
        const m = Math.floor((remaining % 3600000) / 60000)
        const s = Math.floor((remaining % 60000) / 1000)

        return `พร้อมเรียกคืน • หมดเวลาใน ${h} ชม. ${m} น. ${s} ว.`
      }

      return 'พร้อมเรียกคืน'
    }

    const getRemainingDeadline = (r) => {
      if (!r.recallDeadline?.toMillis) return ''

      const remaining = r.recallDeadline.toMillis() - now.value

      if (remaining <= 0) return 'หมดเวลา'

      const h = Math.floor(remaining / 3600000)
      const m = Math.floor((remaining % 3600000) / 60000)
      const s = Math.floor((remaining % 60000) / 1000)

      return `${h} ชม. ${m} น. ${s} ว.`
    }

    const remainingToSearch = (r) => {
      const deployed = r.deployedAt?.toMillis?.() || 0
      const target = deployed + 3 * 3600000
      const remaining = target - now.value

      if (remaining <= 0) return '0 ชม. 0 น. 0 ว.'

      const h = Math.floor(remaining / 3600000)
      const m = Math.floor((remaining % 3600000) / 60000)
      const s = Math.floor((remaining % 60000) / 1000)

      return `${h} ชม. ${m} น. ${s} ว.`
    }

    const normalizeInventoryItem = (item) => {
      const templateId = item?.templateId || item?.name
      const template = gameItems[templateId] || {}
      const states = item?.states || {}

      return {
        ...template,
        ...item,
        ...states,
        name: states.name || item?.name || template.name || templateId || 'ไม่ระบุ',
        type: states.type || item?.type || template.type || '',
        img: states.img || item?.img || template.img || fallbackItem
      }
    }

    const openInventoryModal = (inventory, diamond) => {
      selectedInventory.value = Array.isArray(inventory)
        ? inventory.map(normalizeInventoryItem)
        : []

      selectedDiamond.value = Number(diamond || 0)
      showInventoryModal.value = true
    }

    const toggleConfirmRecall = (id) => {
      clearMessages()
      confirmRecall.value[id] = !confirmRecall.value[id]
    }

    const confirmRecallAction = async (id) => {
      clearMessages()
      isRecalling.value[id] = true

      try {
        const result = await gameActions({
          action: 'manageKillerRobot',
          subAction: 'recall',
          killerRobotId: id
        })

        setSuccess(result?.data?.message || 'เรียกคืนหุ่นยนต์สังหารสำเร็จ')
      } catch (e) {
        setErrorFromCallable(e, 'เรียกคืนหุ่นยนต์ไม่สำเร็จ')
      } finally {
        isRecalling.value[id] = false
        confirmRecall.value[id] = false
      }
    }

    const convertInventory = async (id) => {
      clearMessages()
      isConverting.value[id] = true

      try {
        const result = await gameActions({
          action: 'manageKillerRobot',
          subAction: 'convertInventoryV2',
          killerRobotId: id
        })

        setSuccess(result?.data?.message || 'แปลงรางวัลเป็น v2 สำเร็จ')
      } catch (e) {
        setErrorFromCallable(e, 'แปลงรางวัลเป็น v2 ไม่สำเร็จ')
      } finally {
        isConverting.value[id] = false
        confirmRecall.value[id] = false
      }
    }

    const searchItem = async (id) => {
      clearMessages()
      isSearching.value[id] = true

      try {
        const result = await gameActions({
          action: 'manageKillerRobot',
          subAction: 'searchItem',
          killerRobotId: id
        })

        setSuccess(result?.data?.message || 'ค้นหาไอเทมสำเร็จ')
      } catch (e) {
        setErrorFromCallable(e, 'ค้นหาไอเทมไม่สำเร็จ')
      } finally {
        isSearching.value[id] = false
      }
    }

    return {
      fallbackRobot,
      fallbackItem,
      onRobotImgError,
      onItemImgError,

      now,
      isRecalling,
      isSearching,
      isConverting,
      confirmRecall,

      showInventoryModal,
      selectedInventory,
      selectedDiamond,

      errorMessage,
      successMessage,

      elapsedHours,
      bonusPct,
      calculatedStats,
      currentHp,
      hpPct,
      hpBarClass,

      isRecallable,
      getRemainingTime,
      recallStatusText,
      getRemainingDeadline,
      remainingToSearch,

      hasLegacyInventory,
      legacyInventoryCount,

      openInventoryModal,
      toggleConfirmRecall,
      confirmRecallAction,
      convertInventory,
      searchItem
    }
  }
}
</script>

<style scoped>
.text-xxs {
  font-size: 11px;
  line-height: 1;
}
</style>